import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const PHARMACIST_DEFAULT_PERMISSIONS = ['nrx_sales', 'leave_apply', 'reports_export'];

function isPharmacyAdmin(user: {
  staffRole?: string;
  role?: string;
  staffRoles?: string[];
}): boolean {
  const adminRoles = new Set(['Admin', 'PharmaAdmin', 'EntityAdmin', 'SuperAdmin', 'God']);
  if (adminRoles.has(user.staffRole || '') || adminRoles.has(user.role || '')) return true;
  return Array.isArray(user.staffRoles) && user.staffRoles.some((r) => adminRoles.has(r));
}

function isPharmacist(user: { staffRole?: string; role?: string; staffRoles?: string[] }): boolean {
  if (user.staffRole === 'Pharmacist' || user.role === 'Pharmacist') return true;
  return Array.isArray(user.staffRoles) && user.staffRoles.includes('Pharmacist');
}

function hasPharmacyContext(user: { pharmacyId?: string; clinicId?: string; clinic?: string[] }): boolean {
  return !!(user.pharmacyId || user.clinicId || (Array.isArray(user.clinic) && user.clinic.length > 0));
}

/**
 * True if the current user has the given pharmacy feature permission.
 * Admin (staffRole === 'Admin') has all permissions. Pharmacists get core defaults.
 */
export function useCanAccess(permission: string): boolean {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user) return false;
    if (isPharmacyAdmin(user)) return true;
    if (isPharmacist(user) && PHARMACIST_DEFAULT_PERMISSIONS.includes(permission)) return true;
    if (permission === 'reports_export' && hasPharmacyContext(user)) return true;
    const perms = user.pharmacyPermissions;
    return Array.isArray(perms) && perms.includes(permission);
  }, [user, permission]);
}
